import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule, NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { SharedService } from '../../services/shared.service';
import { ToastrService } from 'ngx-toastr';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { hasAccess } from '../../common/permission/permission';
import { MatTabsModule } from '@angular/material/tabs';
import { NewApiSettingsDialogComponent } from './new/new.component';
import { EditApiSettingDialogComponent } from './edit/edit.component';

@Component({
    selector: 'app-api-settings-admin',
    imports: [
        CommonModule,
        RouterLink,
        MatCardModule,
        MatButtonModule,
        MatMenuModule,
        MatTableModule,
        MatPaginatorModule,
        NgIf,
        MatTooltipModule,
        MatSelectModule,
        MatCheckboxModule,
        FormsModule,
        MatIconModule,
        ReactiveFormsModule,
        MatTabsModule
    ],
    templateUrl: './admin.component.html',
    styleUrl: './admin.component.scss'
})
export class APISettingsAdminComponent implements OnInit {
    user: any = localStorage.getItem('user');

    // isToggled
    isToggled = false;
    loading: boolean = false;
    machines: any = [];
    isDarkTheme: boolean = false;

    apiDisplayedColumns: string[] = ['id', 'api_type_name', 'is_connected', 'connection_checked_at', 'created_by', 'modified_by', 'actions'];
    apiSettingsDataSource: MatTableDataSource<ApiSettingsData> = new MatTableDataSource<ApiSettingsData>([]);

    paginatedData: any = [];
    @ViewChild(MatPaginator) paginatorDivision: MatPaginator;

    currentApiSettingsPage: number = 1;
    itemsPerPage: number = 10;
    totalItems: number = 0;
    startDivisionItem: number = 0;
    endDivisionItem: number = 0;
    sortDivisionBy: string = 'id';
    sortDivisionOrder: string = 'desc';
    searchDivisionValue: string = '';

    contextDivisionMenuVisible = false;
    contextDivisionMenuX = 0;
    contextDivisionMenuY = 0;
    selectedDivisionElement: any;

    isApiSettingsActive: boolean | null = null;
    divisionDetails: any;
    configurations: any = [];

    constructor(private cdf: ChangeDetectorRef, private router: Router, public themeService: CustomizerSettingsService, private sharedService: SharedService, private toastr: ToastrService, private dialog: MatDialog, private http: HttpClient) {
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });
        this.themeService.isDarkThemeEnabled$.subscribe((isDarkThemeEnabled: boolean) => {
            this.isDarkTheme = isDarkThemeEnabled;
        });
    }

    ngOnInit(): void {
        this.user = (JSON.parse(this.user))?.user_info;
        const groups = this.user?.roles;

        if (!localStorage.getItem('token')) {
            localStorage.clear();
            this.router.navigate(['']);
            return;
        }

        if (!this.user.is_superuser) {
            this.toastr.error('You do not have permission to access this page.');
            this.router.navigate(['/dashboard']);
            return;
        }

        const cachedApiSettingsActive = localStorage.getItem('apiSettingsActive');
        if (cachedApiSettingsActive !== null) {
            this.isApiSettingsActive = JSON.parse(cachedApiSettingsActive);
        } else {
            this.isApiSettingsActive = null; // default
        }
        this.getData(this.currentApiSettingsPage)
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    getData(currentPage: number) {
        this.loading = true;
        let endpoint = `page=${currentPage}&item_page=${this.itemsPerPage}&sort_by=${this.sortDivisionBy}&sort_by_dir=${this.sortDivisionOrder}`;

        if (this.isApiSettingsActive != null) {
            endpoint += `&active=${this.isApiSettingsActive}`
        }

        if (this.searchDivisionValue) {
            endpoint += `&search=${this.searchDivisionValue}`;
        }

        this.sharedService.getRequest('/api_settings/all?' + endpoint).subscribe(
            (res: any) => {
                this.loading = false;

                // Ensure response content is an array
                if (!Array.isArray(res.content)) {
                    this.toastr.error('Invalid data format received');
                    return;
                }

                // Assign data to table
                this.apiSettingsDataSource = new MatTableDataSource(res.content);

                // Update pagination details
                this.paginatedData = res.pagination;
                this.totalItems = this.paginatedData.totalItems;
                this.itemsPerPage = this.paginatedData.itemsPerPage;
                this.calculateDivisionItemRange();
            },
            (err) => {
                this.loading = false;
                this.toastr.error('Error: ' + (err.error?.message?.message || 'Unknown error'));
                if (err.error?.message?.message === 'Please login again. Your session is expired') {
                    localStorage.clear();
                    this.router.navigate(['/']);
                }
            }
        );
    }

    onDivisionRightClick(event: MouseEvent, element: any) {
        event.preventDefault();
        this.contextDivisionMenuX = event.clientX;
        this.contextDivisionMenuY = event.clientY;
        this.contextDivisionMenuVisible = true;
        this.selectedDivisionElement = element;
    }

    editAPISettings(id: number) {
        this.contextDivisionMenuVisible = false;
        this.loading = true;
        this.sharedService.getRequest('/api_settings/details?id=' + id).subscribe(
                (res: any) => {
                    this.loading = false;
                    const dialogRef = this.dialog.open(EditApiSettingDialogComponent, {
                        data: { apiDetails: res.content },
                        disableClose: true
                    });
                    const dialogInstance = dialogRef.componentInstance;

                    dialogInstance.submitForm.subscribe((formData: any) => {
                        this.loading = true;
                        const payload = {
                            ...formData,
                            id
                        };

                        this.sharedService.putRequest('/api_settings/update', payload).subscribe(
                                () => {
                                    this.loading = false;
                                    this.toastr.success('API Settings Updated Successfully.');

                                    dialogRef.close();
                                    this.getData(this.currentApiSettingsPage);
                                },
                                (err) => {
                                    this.loading = false;
                                    dialogInstance.stopLoading();

                                    this.toastr.error(
                                        err.error?.message?.message || err.error?.message || 'Failed to update API Settings'
                                    );

                                    if (err.error?.message?.message === 'Please login again. Your session is expired') {
                                        localStorage.clear();
                                        this.router.navigate(['/']);
                                    }
                                }
                            );
                    });
                },
                (err) => {
                    this.loading = false;
                    this.toastr.error(
                        err.error?.message?.message || 'Failed to load API Settings details'
                    );
                }
            );
    }

    newApiSettings() {
        const dialogRef = this.dialog.open(NewApiSettingsDialogComponent, {
            disableClose: true // prevents backdrop / ESC close
        });

        const dialogInstance = dialogRef.componentInstance;

        dialogInstance.submitForm.subscribe((formData: any) => {
            this.loading = true;
            this.sharedService.postRequest('/api_settings/save', formData).subscribe(
                    () => {
                        this.loading = false;
                        dialogInstance.stopLoading();
                        this.toastr.success('API Settings Saved Successfully.');
                        dialogRef.close();
                        this.getData(this.currentApiSettingsPage);
                    },
                    (err) => {
                        this.loading = false;
                        dialogInstance.stopLoading();

                        this.toastr.error(
                            err.error?.message?.message || err.error?.message || 'Failed to save API Settings'
                        );

                        if (err.error?.message?.message === 'Please login again. Your session is expired') {
                            localStorage.clear();
                            this.router.navigate(['/']);
                        }
                    }
                );
        });
    }

    calculateDivisionItemRange(): void {
        if (this.currentApiSettingsPage === 1) {
            this.startDivisionItem = 1;
        } else {
            this.startDivisionItem = (this.currentApiSettingsPage - 1) * this.itemsPerPage + 1;
        }
        this.endDivisionItem = Math.min(this.currentApiSettingsPage * this.itemsPerPage, this.totalItems);
    }

    goToApiSettingsPage(page: number): void {
        if (page !== this.currentApiSettingsPage) {
            this.currentApiSettingsPage = page;
            this.getData(this.currentApiSettingsPage);
        }
    }

    resetApiSettingsSearch() {
        this.searchDivisionValue = '';
        this.getData(1);
    }

    refresh() {
        window.location.reload();
    }

    export() {
        this.loading = true;

        this.sharedService.getRequest('/admin/settings/division/export').subscribe((res: any) => {
            this.downloadFile(res.content.report);
            this.loading = false;
        }, (err) => {
            this.loading = false;
            if (err.status == 449) {
                this.toastr.error('Error: ' + err.error.message);
            }
            else {
                this.toastr.error('Error: ' + err.error.message.message);
                if (err.error.message.message === 'Please login again. Your session is expired') {
                    localStorage.clear();
                    this.router.navigate(['/']);
                }
            }
        });
    }

    downloadFile(url: string): void {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.download = this.getFileNameFromUrl(url); // Optional: extract filename from URL
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    getFileNameFromUrl(url: string): string {
        return url.split('/').pop() || 'download';
    }
}

export interface ApiSettingsData {
    id: number;
    api_type: number;
    api_type_name: string;
    api_key:string;
    api_secret:string;
    application_id:string;
    application_secret:string;
    token:string;
    is_connected:number;
    created_by: string;
    updated_by: string;
    name: string;
    description: string;
    connection_checked_at: string;
}